import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clockTime, meridiem } from '../api/attendance'
import { formatFullDate } from '../api/calendar'
import { askToBePaidForPrivate, type UnpaidPrivate } from '../api/teacher'
import ConfirmAction from './ConfirmAction'

/**
 * Privates she taught before the app started asking, and a way to ask now
 * (D-864).
 *
 * **A closed list, which is why it may sit on the screen she opens most.** A
 * booking has raised its own money ask since 2026-09-14, so this can only hold
 * lessons from before that, and every row she deals with is gone for good. It
 * renders nothing when the list is empty rather than saying *none* — a permanent
 * panel about a temporary problem is clutter on a teacher's home screen.
 *
 * **One at a time, behind a confirmation, and no select-all.** `asked`. Each ask
 * emails every household on the lesson and **cannot be recalled**, so a
 * mis-tap has to cost one message rather than a season's worth. The undo exists
 * (D-863) and is on `/asks`, but an email already read cannot be unread.
 */
export default function UnpaidPrivatesOnHome({ privates }: { privates: UnpaidPrivate[] }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<UnpaidPrivate | null>(null)

  const ask = useMutation({
    mutationFn: (bookingId: string) => askToBePaidForPrivate(bookingId),
    onSettled: () => {
      /*
       * The row leaves the list either way, including when the answer was
       * "already asked" — the studio is in the state she wanted, and a row that
       * stays invites a second tap at a family who has already been emailed.
       */
      setConfirming(null)
      void queryClient.invalidateQueries({ queryKey: ['teacher-home'] })
    },
  })

  if (privates.length === 0) return null

  return (
    <section className="mb-4 w-full rounded-xl border border-line bg-surface px-4 py-3">
      <h2 className="font-display text-xl text-ink">Lessons still to be paid for</h2>

      <p className="mt-1 text-[13px] text-ink-2">
        {privates.length === 1
          ? 'One lesson from earlier this season was never sent a payment request.'
          : `${privates.length} lessons from earlier this season were never sent a payment request.`}{' '}
        Asking emails the family and cannot be undone once they have read it.
      </p>

      <ul className="mt-3 list-none space-y-2 p-0">
        {privates.map((lesson) => (
          <li
            key={lesson.bookingId}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line pt-2"
          >
            <span className="min-w-0 grow text-[13px] text-ink wrap-anywhere">
              <strong>{lesson.dancers || 'A dancer'}</strong>
              {' — '}
              {formatFullDate(lesson.date)} at {clockTime(lesson.start)}
              {meridiem(lesson.start)}
              {lesson.room ? `, ${lesson.room}` : ''}
            </span>

            <button
              type="button"
              className="min-h-[36px] shrink-0 rounded-lg border border-ink-3 bg-surface px-3 text-[13px] text-ink hover:border-ink"
              disabled={ask.isPending}
              onClick={() => setConfirming(lesson)}
            >
              Ask for payment
            </button>
          </li>
        ))}
      </ul>

      {ask.isError && (
        <p className="mt-2 text-[13px] text-warn">
          {ask.error instanceof Error ? ask.error.message : 'That could not be asked.'}
        </p>
      )}

      {confirming && (
        <ConfirmAction
          title="Ask for payment?"
          body={
            `${confirming.dancers || 'This dancer'}'s family will be emailed about the lesson on ` +
            `${formatFullDate(confirming.date)}, with a link to pay you. ` +
            'That email cannot be recalled.'
          }
          confirmLabel="Ask for payment"
          busyLabel="Asking…"
          busy={ask.isPending}
          dismissLabel="Not yet"
          onConfirm={() => ask.mutate(confirming.bookingId)}
          onDismiss={() => setConfirming(null)}
        />
      )}
    </section>
  )
}
